/**
 * Notification Context
 * Provides real-time notification management with Firestore listener
 */

import React, { createContext, useContext, useState, useEffect, useCallback } from 'react';
import { useAuth } from '../hooks/useAuth';
import { apiClient } from '../lib/apiClient';

interface Notification {
  notification_id: string;
  user_id: string;
  type: string;
  title: string;
  message: string;
  link: string;
  read: boolean;
  created_at: string;
  metadata: Record<string, any>;
}

interface NotificationPreferences {
  qbr_completed: boolean;
  insight_generated: boolean;
  health_change: boolean;
  integration_status: boolean;
  mapping_required: boolean;
  team_invitation: boolean;
}

interface NotificationContextType {
  notifications: Notification[];
  unreadCount: number;
  preferences: NotificationPreferences;
  isLoading: boolean;
  error: string | null;
  
  // Actions
  markAsRead: (notificationId: string) => Promise<void>;
  markAllAsRead: () => Promise<void>;
  deleteNotification: (notificationId: string) => Promise<void>;
  updatePreferences: (preferences: NotificationPreferences) => Promise<void>;
  refreshNotifications: () => void;
  refreshUnreadCount: () => Promise<void>; // Lightweight refresh for unread count only
}

const NotificationContext = createContext<NotificationContextType | undefined>(undefined);

export const useNotifications = () => {
  const context = useContext(NotificationContext);
  if (context === undefined) {
    throw new Error('useNotifications must be used within a NotificationProvider');
  }
  return context;
};

interface NotificationProviderProps {
  children: React.ReactNode;
}

export const NotificationProvider: React.FC<NotificationProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [unreadCount, setUnreadCount] = useState(0);
  const [preferences, setPreferences] = useState<NotificationPreferences>({
    qbr_completed: true,
    insight_generated: true,
    health_change: true,
    integration_status: true,
    mapping_required: true,
    team_invitation: true
  });
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Real-time listener for notifications - ONLY depends on user, not auth context changes
  useEffect(() => {
    if (!user) {
      setIsLoading(false);
      return;
    }

    setIsLoading(true);
    setError(null);

    // Load notifications via API instead of Firestore listener
    const loadNotifications = async () => {
      try {
        console.log('Loading notifications...');
        const notifications = await apiClient.getNotifications(50, 0, false);
        console.log('Loaded notifications:', notifications.length);
        setNotifications(notifications);

        const unreadCount = await apiClient.getUnreadCount();
        console.log('Unread count:', unreadCount.count);
        setUnreadCount(unreadCount.count);

        setIsLoading(false);
        setError(null);
      } catch (err) {
        console.error('Failed to load notifications:', err);
        setError('Failed to load notifications');
        setIsLoading(false);
      }
    };

    loadNotifications();

    // No automatic polling - only load when user opens dropdown
    // return () => clearInterval(interval);
  }, [user?.uid]); // Only depend on user.uid, not the entire user object

  // Load notification preferences
  useEffect(() => {
    if (!user) return;

    const loadPreferences = async () => {
      try {
        const prefs = await apiClient.getNotificationPreferences();
        setPreferences(prefs);
      } catch (err) {
        console.error('Failed to load notification preferences:', err);
      }
    };

    loadPreferences();
  }, [user?.uid]); // Only depend on user.uid, not the entire user object

  // Mark notification as read
  const markAsRead = useCallback(async (notificationId: string) => {
    if (!user) return;

    try {
      await apiClient.markAsRead(notificationId);

      // Update local state immediately for better UX
      setNotifications(prev => 
        prev.map(notif => 
          notif.notification_id === notificationId 
            ? { ...notif, read: true }
            : notif
        )
      );
      setUnreadCount(prev => Math.max(0, prev - 1));
    } catch (err) {
      console.error('Failed to mark notification as read:', err);
      setError('Failed to mark notification as read');
    }
  }, [user]);

  // Mark all notifications as read
  const markAllAsRead = useCallback(async () => {
    if (!user) return;

    try {
      await apiClient.markAllAsRead();

      // Update local state immediately
      setNotifications(prev => 
        prev.map(notif => ({ ...notif, read: true }))
      );
      setUnreadCount(0);
    } catch (err) {
      console.error('Failed to mark all notifications as read:', err);
      setError('Failed to mark all notifications as read');
    }
  }, [user]);

  // Delete notification
  const deleteNotification = useCallback(async (notificationId: string) => {
    if (!user) return;

    try {
      // Get the notification before deleting to check if it was unread
      const notificationToDelete = notifications.find(n => n.notification_id === notificationId);

      await apiClient.deleteNotification(notificationId);

      // Update local state immediately
      setNotifications(prev =>
        prev.filter(notif => notif.notification_id !== notificationId)
      );

      // Update unread count if the deleted notification was unread
      if (notificationToDelete && !notificationToDelete.read) {
        setUnreadCount(prev => Math.max(0, prev - 1));
      }
    } catch (err) {
      console.error('Failed to delete notification:', err);
      setError('Failed to delete notification');
    }
  }, [user, notifications]);

  // Update notification preferences
  const updatePreferences = useCallback(async (newPreferences: NotificationPreferences) => {
    if (!user) return;

    try {
      await apiClient.updateNotificationPreferences(newPreferences);
      setPreferences(newPreferences);
    } catch (err) {
      console.error('Failed to update notification preferences:', err);
      setError('Failed to update notification preferences');
    }
  }, [user]);

  // Refresh notifications (force reload) - ONLY when explicitly called
  const refreshNotifications = useCallback(async () => {
    if (!user) return;
    
    console.log('Manually refreshing notifications...');
    setIsLoading(true);
    setError(null);
    
    try {
      const notifications = await apiClient.getNotifications(50, 0, false);
      setNotifications(notifications);
      
      const unreadCount = await apiClient.getUnreadCount();
      setUnreadCount(unreadCount.count);
      
      setError(null);
    } catch (err) {
      console.error('Failed to refresh notifications:', err);
      setError('Failed to load notifications');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Lightweight refresh for unread count only (used after actions that might create notifications)
  const refreshUnreadCount = useCallback(async () => {
    if (!user) return;
    
    try {
      const unreadCount = await apiClient.getUnreadCount();
      setUnreadCount(unreadCount.count);
    } catch (err) {
      console.error('Failed to refresh unread count:', err);
    }
  }, [user]);

  const value: NotificationContextType = {
    notifications,
    unreadCount,
    preferences,
    isLoading,
    error,
    markAsRead,
    markAllAsRead,
    deleteNotification,
    updatePreferences,
    refreshNotifications,
    refreshUnreadCount
  };

  return (
    <NotificationContext.Provider value={value}>
      {children}
    </NotificationContext.Provider>
  );
};
